/**
 * Hook for managing stage cache with hash-based validation
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { checkCacheValidity, getStageData, STAGE_NUMBERS } from '../utils/cacheUtils';

interface UseStageCacheOptions {
  flowId?: string | null;
  stageNumber: number;
  inputData: any;
  enabled?: boolean;
}

interface UseStageCacheResult {
  cachedData: any | null;
  isCheckingCache: boolean;
  isCacheValid: boolean;
  refreshCache: () => void;
  checkCacheManually: () => Promise<void>;
  validateCacheWithInput: () => Promise<void>;
}

export function useStageCache({
  flowId,
  stageNumber,
  inputData,
  enabled = true,
}: UseStageCacheOptions): UseStageCacheResult {
  const [cachedData, setCachedData] = useState<any | null>(null);
  // Start with isCheckingCache as true if we have a flowId, to prevent auto-triggers
  const [isCheckingCache, setIsCheckingCache] = useState(enabled && !!flowId);
  const [isCacheValid, setIsCacheValid] = useState(false);
  const [hasCheckedOnMount, setHasCheckedOnMount] = useState(false);

  // Store the input data for manual cache checking
  const currentInputRef = useCallback(() => inputData, [inputData]);

  const checkCache = useCallback(async () => {
    if (!flowId || !enabled) {
      setCachedData(null);
      setIsCacheValid(false);
      return;
    }

    setIsCheckingCache(true);
    try {
      // For replay mode, just check if stage output exists
      // We don't need to validate hashes when just navigating through saved flows
      const stageData = await getStageData(flowId, stageNumber);
      
      if (stageData && stageData.output) {
        setCachedData(stageData.output);
        setIsCacheValid(true);
        console.log(`✅ Found saved data for stage ${stageNumber} - using cached output`);
      } else {
        setCachedData(null);
        setIsCacheValid(false);
        console.log(`📝 No saved data for stage ${stageNumber} - will process`);
      }
    } catch (error) {
      console.error('Error loading stage data:', error);
      setCachedData(null);
      setIsCacheValid(false);
    } finally {
      setIsCheckingCache(false);
    }
  }, [flowId, stageNumber, enabled]);

  // Only check cache once on mount if we haven't already
  useEffect(() => {
    if (!hasCheckedOnMount && enabled && flowId) {
      setHasCheckedOnMount(true);
      checkCache();
    }
  }, [hasCheckedOnMount, enabled, flowId, checkCache]);

  const refreshCache = () => {
    setCachedData(null);
    setIsCacheValid(false);
    setHasCheckedOnMount(false);
  };

  // Manual check function that can be called by components
  const checkCacheManually = useCallback(async () => {
    await checkCache();
  }, [checkCache]);
  
  // Check cache with hash validation (for when input changes)
  const validateCacheWithInput = useCallback(async () => {
    if (!flowId || !enabled) {
      setCachedData(null);
      setIsCacheValid(false);
      return;
    }

    const currentInput = currentInputRef();
    if (!currentInput) {
      setCachedData(null);
      setIsCacheValid(false);
      return;
    }

    setIsCheckingCache(true);
    try {
      const result = await checkCacheValidity(flowId, stageNumber, currentInput);
      
      console.log(`🔍 Stage ${stageNumber} cache check result:`, result);
      
      if (result.cache_valid) {
        setCachedData(result.cached_output);
        setIsCacheValid(true);
        console.log(`✅ Cache valid for stage ${stageNumber} with current input`);
      } else {
        setCachedData(null);
        setIsCacheValid(false);
        console.log(`❌ Cache invalid for stage ${stageNumber} - input has changed`);
      }
    } catch (error) {
      console.error('Error validating cache:', error);
      setCachedData(null);
      setIsCacheValid(false);
    } finally {
      setIsCheckingCache(false);
    }
  }, [flowId, stageNumber, currentInputRef, enabled]);

  return {
    cachedData,
    isCheckingCache,
    isCacheValid,
    refreshCache,
    checkCacheManually,
    validateCacheWithInput,
  };
}